import { useTranslation } from 'next-i18next/pages';

import { Checkbox, Form } from 'antd';

import { TranslationNamespace } from '@/i18n';

import { CompactInputNumber } from '../CompactInputNumber';
import { StepActions } from '../StepActions';

import styles from '@/components/TariffCalculatorNew/TariffCalculator.module.scss';

interface FeatureChoiceProps {
	name: string;
	title: string;
	description: string;
	platinum?: boolean;
}

function FeatureChoice({ name, title, description, platinum = false }: FeatureChoiceProps) {
	return (
		<Form.Item name={name} valuePropName="checked" noStyle>
			<Checkbox className={`${styles.choice}${platinum ? ` ${styles.platinum}` : ''}`}>
				<span className={styles.choiceText}>
					<strong>{title}</strong>
					<small>{description}</small>
				</span>
			</Checkbox>
		</Form.Item>
	);
}

interface StepFeaturesProps {
	onPrev: () => void;
}

export function StepFeatures({ onPrev }: StepFeaturesProps) {
	const { t } = useTranslation(TranslationNamespace.Calculator);
	return (
		<section className={styles.stepPanel}>
			<h2 className={styles.stepTitle}>{t('features.title')}</h2>
			<p className={styles.stepLead}>{t('features.description')}</p>

			<div className={`${styles.fieldGrid} ${styles.fieldGridTight}`}>
				<Form.Item name="warehouses" label={t('features.warehouses')}>
					<CompactInputNumber min={0} step={1} addon={t('units.title')} size="large" />
				</Form.Item>
				<Form.Item name="users" label={t('features.users')}>
					<CompactInputNumber min={1} step={1} addon={t('units.people')} size="large" />
				</Form.Item>
			</div>

			<div className={styles.choiceGrid}>
				<FeatureChoice
					name="featureTrade"
					title={t('features.trade')}
					description={t('features.trade-description')}
				/>
				<FeatureChoice
					name="featureStock"
					title={t('features.retention')}
					description={t('features.retention-description')}
				/>
				<FeatureChoice
					name="featureLogistics"
					title={t('features.logistics')}
					description={t('features.logistics-description')}
				/>
				<FeatureChoice
					name="featureCrm"
					title={t('features.crm')}
					description={t('features.crm-description')}
				/>
				<FeatureChoice
					name="featureCommunication"
					title={t('features.communication')}
					description={t('features.communication-description')}
				/>
				<FeatureChoice
					name="featureDocuments"
					title={t('features.documents')}
					description={t('features.documents-description')}
				/>
				<FeatureChoice
					name="featureOtc"
					title={t('features.otc')}
					description={t('features.otc-description')}
				/>
				<FeatureChoice
					name="featureAnalytics"
					title={t('features.analytics')}
					description={t('features.tariff-dependent')}
				/>
				<FeatureChoice
					name="featureDashboard"
					title={t('features.dashboard')}
					description={t('features.dashboard-description')}
				/>
				<FeatureChoice
					name="personalManager"
					title={t('features.manager')}
					description={t('features.manager-description')}
					platinum
				/>
				<FeatureChoice
					name="marketingSupport"
					title={t('features.marketing')}
					description={t('features.marketing-description')}
					platinum
				/>
				<FeatureChoice
					name="featureTraining"
					title={t('features.training')}
					description={t('features.tariff-dependent')}
				/>
				<FeatureChoice
					name="featureApi"
					title={t('features.api')}
					description={t('features.api-description')}
				/>
			</div>

			<p className={styles.privacy}>{t('features.privacy')}</p>
			<StepActions onPrev={onPrev} nextLabel={t('features.calculate')} nextHtmlType="submit" />
		</section>
	);
}
